import flashLightning from '@/images/FlashLightning.svg';
import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import { useAuth } from '@/services/auth';

import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import SimpleNotificationModal from '@/components/Modals/SimpleNotificationModal';
import RemoveWatermarkToggle from '@/components/RemoveWatermarkToggle/RemoveWatermarkToggle';
import { useRemoveWatermarkAccess } from '@/hooks/useRemoveWatermarkAccess';
import { ResetPassword } from '@/pages/account-settings/components/ProfileDetails/ResetPassword';
import { UploadLogo } from '@/pages/account-settings/components/ProfileDetails/UploadLogo';
import { useUserSettings } from '@/state/user-settings/UserSettingsProvider';
import { PlanGroup } from '@d-id/types';
import { useTheme } from '@emotion/react';
import Card from '../Card';

const Title = styled(Typography)`
    font-style: normal;
    font-weight: 700;
    margin-top: 1.6rem;
    margin-bottom: 2.4rem;
`;

const StyledH5 = styled(Typography)`
    color: ${({ theme }) => theme.colors.secondary[500]};
    display: flex;
    margin-bottom: 8px;
    font-weight: 600;
    align-items: center;
    gap: 4px;

    svg {
        color: ${({ theme }) => theme.colors.primary[900]};
    }
`;

const Avatar = styled.img`
    width: 72px;
    height: 72px;
    object-fit: cover;
    border-radius: 100%;
`;

const SectionContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 16px;
    margin-bottom: 24px;
`;

const TextContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    justify-content: center;
    max-width: 250px;
`;

const TextBlack = styled(Typography)`
    color: ${({ theme }) => theme.colors.black[900]};
`;

const TextLight = styled(Typography)`
    color: ${({ theme }) => theme.colors.secondary[700]};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    :hover {
        color: ${({ theme }) => theme.colors.primary[950]};
    }
`;

const RemoveWatermarkContainer = styled.div`
    border: 1px solid ${({ theme }) => theme.colors.black[300]};
    border-radius: 0.8rem;
    display: flex;
    align-items: center;
    padding: 1.6rem;
    margin-bottom: 1.6rem;
`;

const PremiumBadge = styled(MaskedIcon)`
    margin-left: 0.8rem;
    margin-right: 0.4rem;
`;

interface ProfileDetailsProps {
    picture?: string;
    name?: string;
    email?: string;
    flex: number;
}

export const ProfileDetails = ({ picture = '', name = '', email = '', flex }: ProfileDetailsProps) => {
    const { isAnonymous, user } = useAuth();
    const [openMobileModal, setOpenMobileModal] = useState(false);
    const { userSettings, updateUserSettings } = useUserSettings();
    const [removeWatermarkSelected, setRemoveWatermarkSelected] = useState(!!userSettings?.preferences?.removeWatermark);
    const { showRemoveWatermark, removeWatermarkBlocked } = useRemoveWatermarkAccess(user);
    const theme = useTheme();

    useEffect(() => {
        updateUserSettings({ preferences: { removeWatermark: removeWatermarkSelected } });
    }, [removeWatermarkSelected]);

    const isLogoAvailable =
        (user?.features || []).includes('logo') ||
        user?.planGroup === PlanGroup.SCALE ||
        user?.planGroup === PlanGroup.ADVANCED ||
        user?.planGroup === PlanGroup.ENTERPRISE;

    return (
        <>
            <Card flex={flex} gap={0} hide={Boolean(isAnonymous)} bg="white">
                <Title variant="h3">Details</Title>
                <div>
                    <StyledH5 variant="h5">Personal</StyledH5>
                    <SectionContainer>
                        <Avatar src={picture} />
                        <TextContainer>
                            <TextBlack variant="h4">{name.trim() || '- -'}</TextBlack>
                            <TextLight variant="body2" title={email.trim()}>
                                {email.trim() || '- -'}
                            </TextLight>
                        </TextContainer>
                    </SectionContainer>
                </div>

                <div>
                    <StyledH5 variant="h5">
                        Company Logo
                        {!isLogoAvailable && <MaskedIcon src={flashLightning} size="16px" />}
                    </StyledH5>
                    <SectionContainer>
                        <UploadLogo />
                    </SectionContainer>
                </div>

                {showRemoveWatermark && (
                    <>
                        <StyledH5 variant="h5">Watermark</StyledH5>
                        <RemoveWatermarkContainer>
                            <RemoveWatermarkToggle
                                blocked={removeWatermarkBlocked}
                                checked={removeWatermarkSelected}
                                onChange={enabled => {
                                    setRemoveWatermarkSelected(enabled);
                                }}
                            />
                            <PremiumBadge
                                src={flashLightning}
                                size="16px"
                                color={removeWatermarkBlocked ? theme.colors.primary.main : theme.colors.secondary.main}
                            />
                            <Typography variant="body1" fontWeight={500}>
                                Remove watermark
                            </Typography>
                        </RemoveWatermarkContainer>
                    </>
                )}

                <ResetPassword />
            </Card>

            <SimpleNotificationModal
                open={openMobileModal}
                onClose={() => {
                    setOpenMobileModal(false);
                }}
                content="Please use the mobile app to manage your plan."
                primaryActionContent="Got It"
                onPrimaryAction={() => {
                    setOpenMobileModal(false);
                }}
                buttonPlacement="center"
            />
        </>
    );
};
